'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useParams, usePathname, useRouter } from 'next/navigation';
import useSWR, { mutate } from 'swr';
import { useMediaQuery } from '@/hooks/use-media-query';
import { Channel, Guild } from '@/types';

interface DashboardContextType {
  guilds: Guild[];
  channels: Channel[];
  isLoading: boolean;
  isLoadingGuilds: boolean;
  isLoadingChannels: boolean;
  isLoadingCurrentGuild: boolean;
  selectGuild: (guildId: string) => void;
  selectChannel: (guildId: string, channelId: string) => void;
  currentGuildId: string | null;
  currentChannelId: string | null;
  currentGuild: Guild | null;
  currentChannel: Channel | null;
  error: any;
  isDesktop: boolean;
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
  closeSidebar: () => void; // 단순히 사이드바를 닫는 함수
  handleSidebarOpenChange: (open: boolean) => void; // open 상태에 따라 사이드바를 제어하는 함수
  openSidebar: () => void;
  refreshGuilds: () => Promise<void>; // 새로고침 함수 추가
}

const DashboardContext = createContext<DashboardContextType | undefined>(undefined);

// API 요청을 위한 fetcher 함수
const fetcher = (url: string) => fetch(url).then(res => {
  if (!res.ok) throw new Error('API 요청 실패');
  return res.json();
}).then(data => data.data);

export const DashboardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // URL 파라미터에서 직접 ID 가져오기 (Single Source of Truth)
  const params = useParams();
  const currentGuildId = params?.guildId as string || null;
  const currentChannelId = params?.channelId as string || null;

  const router = useRouter();

  // 사용자 서버 목록 가져오기
  const {
    data: guilds = [],
    error: guildsError,
    isLoading: isLoadingGuilds
  } = useSWR<Guild[]>('/api/users/@me/guilds', fetcher);

  // 현재 선택된 서버 정보 가져오기
  const {
    data: currentGuild = null,
    error: currentGuildError,
    isLoading: isLoadingCurrentGuild
  } = useSWR<Guild | null>(
    currentGuildId ? `/api/guilds/${currentGuildId}` : null,
    fetcher
  );

  // 선택된 서버의 채널 목록 가져오기
  const {
    data: channels = [],
    error: channelsError,
    isLoading: isLoadingChannels
  } = useSWR<Channel[]>(
    currentGuildId ? `/api/guilds/${currentGuildId}/channels` : null,
    fetcher
  );

  // 현재 선택된 채널 찾기
  const currentChannel = channels.find(channel => channel.id === currentChannelId) || null;

  // 라우팅 메서드만 제공 (내부 상태 없음)
  const selectGuild = (guildId: string) => {
    console.log('Navigating to Guild ID:', guildId);
    router.push(`/dashboard/${guildId}`);
  };

  const selectChannel = (guildId: string, channelId: string) => {
    console.log('Navigating to Channel ID:', channelId, 'in Guild:', guildId);
    router.push(`/dashboard/${guildId}/channels/${channelId}`);
  };

  // 서버 목록 새로고침 함수 추가
  const refreshGuilds = async () => {
    try {
      await mutate('/api/users/@me/guilds');
    } catch (error) {
      console.error('서버 목록 새로고침 실패:', error);
    }
  };

  // 사이드바 상태 관리
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  // 커스텀 미디어 쿼리 훅 사용
  const isDesktop = useMediaQuery("(min-width: 768px)");

  useEffect(() => {
    setIsSidebarOpen(isDesktop);
  }, [isDesktop]);

  const toggleSidebar = () => setIsSidebarOpen(prev => !prev);

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  const handleSidebarOpenChange = (open: boolean) => {
    if (!open && !isDesktop) {
      setIsSidebarOpen(false);
    }
  };

  const openSidebar = () => {
    if (!isDesktop) {
      setIsSidebarOpen(true);
    }
  };

  const isLoading = isLoadingGuilds || isLoadingChannels || isLoadingCurrentGuild;

  return (
    <DashboardContext.Provider value={{
      guilds,
      channels,
      isLoading,
      isLoadingGuilds,
      isLoadingChannels,
      isLoadingCurrentGuild,
      selectGuild,
      selectChannel,
      currentGuildId,
      currentChannelId,
      currentGuild,
      currentChannel,
      error: guildsError || channelsError || currentGuildError,
      isSidebarOpen,
      isDesktop,
      toggleSidebar,
      closeSidebar,
      handleSidebarOpenChange,
      openSidebar,
      refreshGuilds, // 새로고침 함수 추가
    }}>
      {children}
    </DashboardContext.Provider>
  );
}

export function useDashboard() {
  const context = useContext(DashboardContext);
  if (context === undefined) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
}
